



































import { Component, Vue, Watch, Ref } from "vue-property-decorator";

import { Task, ScheduledTask, FocusedTask } from "@/data/models/Task";
import { UserLookup } from "@/data/models/UserLookup";
import { loadUsers } from "@/data/repository/UserRepository";
import { MovingTaskData } from "@/data/models/MovingTaskData";

import { loadSchedulingSettings } from "@/data/repository/SchedulingSettingsRepository";
import { getRunningDryingProjectOverviews } from "@/data/repository/ProjectOverviewRepository";

import { SchedulingModule } from "@/store/modules/SchedulingModule";

import { Role } from "@/data/models/User";
import { formatUserRole } from "@/utils/LocalisationHelper";

import { DataGroup } from "vis-timeline/standalone";
import { TimeSlot } from "@/utils/scheduling/TimeSlot";
import { generateRandomUuid } from "@/utils/Utils";
import { getTasks } from "@/data/repository/TasksRepository";

import * as _ from "lodash";

import TaskListComponent from "@/components/scheduling/TaskList.vue";
import TaskDetailsComponent from "@/components/scheduling/TaskDetails.vue";
import TimelineComponent from "@/components/scheduling/timeline/Timeline.vue";
import { findNextFittingTimeSlot } from "@/utils/scheduling/SchedulingUtils";
import { calculateObjectiveFunctionRelativeToFocusedTask } from "@/utils/scheduling/SchedulingObjectiveFunction";

interface NewScheduledTaskData {
  start: string;
  group: string;
  task: Task;
}

interface ScheduledTaskChangedData {
  id: string;
  group: string;
  start: string;
  end: string;
}

@Component({
  name: "SchedulingComponent",
  components: {
    "task-list": TaskListComponent,
    "task-details": TaskDetailsComponent,
    timeline: TimelineComponent,
  },
})
export default class SchedulingComponent extends Vue {
  @Ref("taskDetails")
  taskDetailsComponent!: TaskDetailsComponent;

  get scheduledTasks(): ScheduledTask[] {
    return SchedulingModule.scheduledTasksToDisplay;
  }

  get userLookup() {
    return SchedulingModule.userLookup;
  }

  get highlightedTaskId() {
    return SchedulingModule.highlightedTaskId;
  }

  get focusedTask() {
    return SchedulingModule.focusedTask;
  }

  get showConfirmedTaskDetails() {
    return SchedulingModule.focusedTask?.wasConfirmed ?? false;
  }

  get timelineConfiguration() {
    return SchedulingModule.timelineConfiguration;
  }

  get schedulingSettings() {
    return SchedulingModule.schedulingSettings;
  }

  groups: DataGroup[] = [];

  movingTaskData: MovingTaskData | null = null;

  @Watch("focusedTask")
  onFocusedTaskChanged(
    newFocusedTask: FocusedTask | null,
    oldFocusedTask: FocusedTask | null
  ) {
    const oldRequiredQualification = oldFocusedTask?.requiredQualification;
    const newRequiredQualification = newFocusedTask?.requiredQualification;

    const didRequierementsChange = !_.isEqual(
      oldRequiredQualification,
      newRequiredQualification
    );

    if (!didRequierementsChange) {
      return;
    }

    this.groups = this.calculateUserGroups();
  }

  showCustomTimeline = false;

  overlapCheckTimeslot: TimeSlot = {
    dayId: "",
    startTimestamp: 0,
    durationInMs: 0,
  };

  onTaskMoving(movingTaskData: MovingTaskData) {
    const focusedTask = this.focusedTask;

    if (focusedTask && !focusedTask?.isMoving) {
      SchedulingModule.updateFocusedTask({
        id: movingTaskData.id,
        isMoving: true,
      });
    }

    const currentMovingTaskData = this.movingTaskData;

    if (currentMovingTaskData) {
      if (
        currentMovingTaskData.id != movingTaskData.id ||
        currentMovingTaskData.startDate.getTime() !=
          movingTaskData.startDate.getTime() ||
        currentMovingTaskData.userId != movingTaskData.userId
      ) {
        this.movingTaskData = {
          ...movingTaskData,
        };
      }
    } else {
      this.movingTaskData = movingTaskData;
    }
  }

  onTaskMoved(data: ScheduledTaskChangedData) {
    const focusedTask = this.focusedTask;
    if (!focusedTask) {
      return;
    }

    const taskId = focusedTask.id;
    const startDate = new Date(data.start);
    const userId = data.group;

    const timeSlot = findNextFittingTimeSlot(
      userId,
      taskId,
      startDate,
      focusedTask
    );

    if (!timeSlot) {
      SchedulingModule.clearSchedulingInfo({
        taskId,
      });

      return;
    }

    const didUserIdChange = userId !== focusedTask.userId;
    const newIsTargetUserFixed = didUserIdChange
      ? false
      : focusedTask.planningParameters.isTargetUserFixed ?? false;

    const updatedPlanningParameters = {
      ...focusedTask.planningParameters,
      isTargetUserFixed: newIsTargetUserFixed,
    };

    const fittedStartDate = new Date(timeSlot.startTimestamp);
    const fittedEndDate = new Date(
      timeSlot.startTimestamp + timeSlot.durationInMs
    );

    SchedulingModule.updateFocusedTask({
      id: taskId,
      userId: userId,
      schedule: {
        startDate: fittedStartDate,
        endDate: fittedEndDate,
      },
      isMoving: false,
      updateScheduledTask: true,
      calculateScore: true,
      planningParameters: updatedPlanningParameters,
    });

    const updatedScheduledTask = SchedulingModule.tasksOfAllUsersLookup.getScheduledTask(
      taskId
    );

    this.movingTaskData = null;

    if (updatedScheduledTask) {
      this.taskDetailsComponent.updateSchedulingInfoOfSelectedTask(
        updatedScheduledTask
      );
    }
  }

  async onRemoveScheduledTask(taskId: string) {
    await this.taskDetailsComponent.onRemoveScheduledTask(taskId);
  }

  onAddScheduledTask(taskData: NewScheduledTaskData) {
    if (!taskData.task) {
      return;
    }

    const focusedTask = this.focusedTask;

    if (!focusedTask) {
      return;
    }

    const userId = taskData.group;

    if (!SchedulingModule.userLookup.getUser(userId)) {
      return;
    }

    const start = new Date(taskData.start);

    const nextFittingTimeSlot = findNextFittingTimeSlot(
      userId,
      focusedTask.id,
      start,
      focusedTask
    );

    if (nextFittingTimeSlot) {
      const newScheduledTask = {
        ...focusedTask,
        id: generateRandomUuid(),
        wasConfirmed: false,
        userId: userId,
        startDate: new Date(nextFittingTimeSlot.startTimestamp),
        endDate: new Date(
          nextFittingTimeSlot.startTimestamp + nextFittingTimeSlot.durationInMs
        ),
        planningParameters: focusedTask.planningParameters ?? {
          isDateFixed: false,
          isTimeFixed: false,
          isUserFixed: false,
        },
      };

      newScheduledTask.schedulingScore = calculateObjectiveFunctionRelativeToFocusedTask(
        newScheduledTask
      );

      this.taskDetailsComponent.addSuggestAppointment(newScheduledTask);
    }
  }

  async created() {
    const result = await Promise.all([
      loadUsers(),
      getRunningDryingProjectOverviews(),
      loadSchedulingSettings(),
    ]);

    const users = result[0];
    const userLookup = new UserLookup(users);

    const runningDryingProjectOverviews = result[1];
    const schedulingSettings = result[2];

    const { tasksToSchedule, scheduledTasks } = getTasks(
      runningDryingProjectOverviews,
      userLookup,
      schedulingSettings
    );

    SchedulingModule.setInitialData({
      userLookup: userLookup,
      tasksToSchedule: tasksToSchedule,
    });

    SchedulingModule.setSchedulingSettings(schedulingSettings);
    SchedulingModule.setScheduledTasks(scheduledTasks);

    this.groups = this.calculateUserGroups();
  }

  onTaskSelected(taskId: string) {
    const focusedTask = this.focusedTask;

    if (taskId == focusedTask?.id) {
      return;
    }

    const scheduledTask = SchedulingModule.tasksOfAllUsersLookup.getScheduledTask(
      taskId
    );

    if (scheduledTask) {
      SchedulingModule.saveFocusedTaskChanges();
      SchedulingModule.setFocusedTask({
        ...scheduledTask,
      });

      return;
    }
  }

  roleGroupPrefix = "_role_";

  calculateUserGroups() {
    const roles = [
      Role.TECHNICIAN_AND_LEAK_DETECTION,
      Role.TECHNICIAN_2,
      Role.TECHNICIAN_1,
      Role.LEAK_DETECTION,
      Role.OFFICE,
      Role.PROJECT_MANAGER,
      Role.ADMIN,
    ];

    const rolesToUserId = new Map<Role, string[]>();

    const userGroups = SchedulingModule.usersQualifiedForFocusedTask.map(
      (user) => {
        const role = user.role;

        if (role) {
          let list = rolesToUserId.get(role);

          if (!list) {
            list = [];
            rolesToUserId.set(role, list);
          }

          list.push(user.id);
        }

        return {
          content: user.nickname,
          id: user.id,
          treeLevel: 2,
        };
      }
    );

    const roleGroups = roles
      .filter((role) => {
        const userIdsOfRole = rolesToUserId.get(role);
        return userIdsOfRole && userIdsOfRole.length > 0;
      })
      .map((role) => {
        const userIdsOfRole = rolesToUserId.get(role) ?? [];

        return {
          content: formatUserRole(this, role),
          id: `${this.roleGroupPrefix}${role}`,
          nestedGroups: userIdsOfRole,
          showNested: true,
          treeLevel: 1,
        };
      });

    return [...userGroups, ...roleGroups];
  }
}
